import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { getReportedReviews, dismissReport, deleteReview, warnUser, banUser } from '@/services/api';
import { toast } from 'sonner';
import { 
  Shield, 
  Flag, 
  Trash2, 
  AlertTriangle, 
  Ban, 
  CheckCircle, 
  Star, 
  User, 
  Calendar,
  Mail,
  Phone,
  MapPin,
  MessageSquare,
  XCircle,
  BarChart3
} from 'lucide-react';

export default function AdminDashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  // Check if user is admin (scott@badasshair.com)
  const isAdmin = user?.email === 'scott@badasshair.com' || user?.is_admin;

  useEffect(() => {
    if (!isAdmin) {
      toast.error('Access denied');
      navigate('/dashboard');
      return;
    }

    const fetchReports = async () => {
      try {
        const data = await getReportedReviews();
        setReports(data);
      } catch (error) {
        console.error('Error fetching reports:', error);
        if (error.response?.status === 403) {
          toast.error('Admin access required');
          navigate('/dashboard');
        } else {
          toast.error('Failed to load reported reviews');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
  }, [isAdmin, navigate]);

  const handleDismiss = async (reviewId) => {
    setActionLoading(`dismiss-${reviewId}`);
    try {
      await dismissReport(reviewId);
      setReports(reports.filter(r => r.review.id !== reviewId));
      toast.success('Report dismissed');
    } catch (error) {
      toast.error('Failed to dismiss report');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (reviewId) => {
    if (!window.confirm('Are you sure you want to permanently delete this review? This cannot be undone.')) {
      return;
    }
    
    setActionLoading(`delete-${reviewId}`);
    try {
      await deleteReview(reviewId);
      setReports(reports.filter(r => r.review.id !== reviewId));
      toast.success('Review deleted');
    } catch (error) {
      toast.error('Failed to delete review');
    } finally {
      setActionLoading(null);
    }
  };

  const handleWarn = async (userId, email) => {
    setActionLoading(`warn-${userId}`);
    try {
      await warnUser(userId);
      toast.success(`Warning sent to ${email}`);
    } catch (error) {
      toast.error('Failed to send warning');
    } finally {
      setActionLoading(null);
    }
  };

  const handleBan = async (userId, email) => {
    if (!window.confirm(`Are you sure you want to ban ${email}? They will no longer be able to log in.`)) {
      return;
    }
    
    setActionLoading(`ban-${userId}`);
    try {
      await banUser(userId);
      toast.success(`${email} has been banned`);
      // Remove their reports from the list
      setReports(reports.filter(r => r.reviewer?.id !== userId));
    } catch (error) {
      toast.error('Failed to ban user');
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isAdmin) {
    return null;
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D97757]"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
              <Shield className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <h1 className="font-heading text-3xl font-semibold text-stone-900">
                Admin Dashboard
              </h1>
              <p className="text-stone-600">Manage reported reviews</p>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <Card className="border-stone-100">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <Flag className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-stone-900">{reports.length}</p>
                  <p className="text-sm text-stone-600">Pending Reports</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-stone-100">
            <CardContent className="p-6">
              <button
                onClick={() => window.location.href = '/admin/credentials'}
                className="flex items-center gap-4 w-full text-left hover:opacity-80 transition-opacity"
              >
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <User className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-lg font-semibold text-stone-900">Credentials</p>
                  <p className="text-sm text-stone-600">Review signups →</p>
                </div>
              </button>
            </CardContent>
          </Card>

          <Card className="border-stone-100">
            <CardContent className="p-6">
              <button
                onClick={() => window.location.href = '/admin/analytics'}
                className="flex items-center gap-4 w-full text-left hover:opacity-80 transition-opacity"
                data-testid="analytics-link"
              >
                <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-lg font-semibold text-stone-900">Analytics</p>
                  <p className="text-sm text-stone-600">View app stats →</p>
                </div>
              </button>
            </CardContent>
          </Card>
        </div>

        {/* Reports List */}
        {reports.length === 0 ? (
          <Card className="border-stone-100">
            <CardContent className="p-12 text-center">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-stone-900 mb-2">All Clear!</h3>
              <p className="text-stone-600">No reported reviews to review at this time.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {reports.map((item) => (
              <Card key={item.report.id} className="border-stone-100 shadow-sm overflow-hidden">
                <div className={`px-6 py-3 border-b ${item.report.reporter_id === 'system' ? 'bg-purple-50 border-purple-100' : 'bg-red-50 border-red-100'}`}>
                  <div className="flex items-center justify-between">
                    <div className={`flex items-center gap-2 ${item.report.reporter_id === 'system' ? 'text-purple-700' : 'text-red-700'}`}>
                      {item.report.reporter_id === 'system' ? (
                        <>
                          <Shield className="w-4 h-4" />
                          <span className="font-medium">Auto-Flagged by System</span>
                        </>
                      ) : (
                        <>
                          <Flag className="w-4 h-4" />
                          <span className="font-medium">Reported Review</span>
                        </>
                      )}
                    </div>
                    <span className={`text-sm ${item.report.reporter_id === 'system' ? 'text-purple-600' : 'text-red-600'}`}>
                      {item.report.reporter_id === 'system' ? 'Flagged' : 'Reported'} on {formatDate(item.report.created_at)}
                    </span>
                  </div>
                </div>
                
                <CardContent className="p-6">
                  {/* Report Details */}
                  <div className={`mb-6 p-4 rounded-lg border ${item.report.reporter_id === 'system' ? 'bg-purple-50 border-purple-200' : 'bg-amber-50 border-amber-200'}`}>
                    <h4 className={`font-medium mb-2 flex items-center gap-2 ${item.report.reporter_id === 'system' ? 'text-purple-800' : 'text-amber-800'}`}>
                      <AlertTriangle className="w-4 h-4" />
                      {item.report.reporter_id === 'system' ? 'Auto-Flag Reason' : 'Report Reason'}
                    </h4>
                    <p className={`capitalize ${item.report.reporter_id === 'system' ? 'text-purple-700' : 'text-amber-700'}`}>
                      {item.report.reason === 'auto_flagged' ? 'Potentially inappropriate content detected' : (item.report.reason || 'Inappropriate content')}
                    </p>
                    {item.report.details && (
                      <p className={`text-sm mt-1 ${item.report.reporter_id === 'system' ? 'text-purple-600' : 'text-amber-600'}`}>{item.report.details}</p>
                    )}
                    {item.reporter && item.report.reporter_id !== 'system' && (
                      <p className="text-xs text-amber-600 mt-2">
                        Reported by: {item.reporter.full_name} ({item.reporter.email})
                      </p>
                    )}
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Review Content */}
                    <div>
                      <h4 className="font-medium text-stone-900 mb-3 flex items-center gap-2">
                        <MessageSquare className="w-4 h-4" />
                        Review Content
                      </h4>
                      <div className="p-4 bg-stone-50 rounded-lg">
                        <div className="flex items-center gap-2 mb-3">
                          <Star className="w-5 h-5 fill-[#D97757] text-[#D97757]" />
                          <span className="font-semibold text-stone-900">
                            {item.review?.overall_rating?.toFixed(1) || 'N/A'}
                          </span>
                          <span className="text-sm text-stone-500">overall rating</span>
                        </div>
                        
                        {item.review?.general_notes && (
                          <p className="text-stone-700 mb-2">{item.review.general_notes}</p>
                        )}
                        
                        {(item.review?.service_notes || item.review?.hair_notes) && (
                          <div className="mt-2 p-2 bg-white rounded border border-stone-200">
                            <p className="text-xs font-medium text-stone-500 uppercase mb-1">Service Notes</p>
                            <p className="text-sm text-stone-600">{item.review.service_notes || item.review.hair_notes}</p>
                          </div>
                        )}
                        
                        <p className="text-xs text-stone-500 mt-3">
                          <Calendar className="w-3 h-3 inline mr-1" />
                          Posted on {formatDate(item.review?.created_at)}
                        </p>
                      </div>
                    </div>

                    {/* People Involved */}
                    <div className="space-y-4">
                      {/* Client Info */}
                      <div>
                        <h4 className="font-medium text-stone-900 mb-2">Client Being Reviewed</h4>
                        <div className="p-3 bg-stone-50 rounded-lg">
                          <p className="font-medium text-stone-900">
                            {item.client?.first_name} {item.client?.last_name || item.client?.last_initial || ''}
                          </p>
                          {item.client?.phone && (
                            <p className="text-sm text-stone-600 flex items-center gap-1 mt-1">
                              <Phone className="w-3 h-3" /> {item.client.phone}
                            </p>
                          )}
                          {item.client?.location && (
                            <p className="text-sm text-stone-600 flex items-center gap-1">
                              <MapPin className="w-3 h-3" /> {item.client.location}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Reviewer Info */}
                      <div>
                        <h4 className="font-medium text-stone-900 mb-2">Reviewer</h4>
                        <div className="p-3 bg-stone-50 rounded-lg">
                          {item.review?.is_anonymous && (
                            <div className="flex items-center gap-2 mb-2 text-amber-600 text-sm">
                              <User className="w-4 h-4" />
                              <span className="font-medium">Posted Anonymously</span>
                              <span className="text-amber-500">(visible to admin only)</span>
                            </div>
                          )}
                          <p className="font-medium text-stone-900">
                            {item.reviewer?.full_name || 'Unknown'}
                          </p>
                          <p className="text-sm text-stone-600 flex items-center gap-1 mt-1">
                            <Mail className="w-3 h-3" /> {item.reviewer?.email || 'No email'}
                          </p>
                          {item.reviewer?.phone && (
                            <p className="text-sm text-stone-600 flex items-center gap-1">
                              <Phone className="w-3 h-3" /> {item.reviewer.phone}
                            </p>
                          )}
                          <p className="text-xs text-stone-500 mt-2">
                            Account created: {formatDate(item.reviewer?.created_at)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="mt-6 pt-6 border-t border-stone-200">
                    <h4 className="font-medium text-stone-900 mb-3">Actions</h4>
                    <div className="flex flex-wrap gap-3">
                      <Button
                        onClick={() => handleDismiss(item.review.id)}
                        disabled={actionLoading === `dismiss-${item.review.id}`}
                        variant="outline"
                        className="h-10 px-4 rounded-lg border-green-300 text-green-700 hover:bg-green-50"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        {actionLoading === `dismiss-${item.review.id}` ? 'Dismissing...' : 'Dismiss Report'}
                      </Button>
                      
                      <Button
                        onClick={() => handleDelete(item.review.id)}
                        disabled={actionLoading === `delete-${item.review.id}`}
                        variant="outline"
                        className="h-10 px-4 rounded-lg border-red-300 text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        {actionLoading === `delete-${item.review.id}` ? 'Deleting...' : 'Delete Review'}
                      </Button>
                      
                      {item.reviewer && (
                        <>
                          <Button
                            onClick={() => handleWarn(item.reviewer.id, item.reviewer.email)}
                            disabled={actionLoading === `warn-${item.reviewer.id}`}
                            variant="outline"
                            className="h-10 px-4 rounded-lg border-amber-300 text-amber-700 hover:bg-amber-50"
                          >
                            <AlertTriangle className="w-4 h-4 mr-2" />
                            {actionLoading === `warn-${item.reviewer.id}` ? 'Warning...' : 'Warn User'}
                          </Button>
                          
                          <Button
                            onClick={() => handleBan(item.reviewer.id, item.reviewer.email)}
                            disabled={actionLoading === `ban-${item.reviewer.id}`}
                            className="h-10 px-4 rounded-lg bg-red-600 text-white hover:bg-red-700"
                          >
                            <Ban className="w-4 h-4 mr-2" />
                            {actionLoading === `ban-${item.reviewer.id}` ? 'Banning...' : 'Ban User'}
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
